import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../../core/providers/AuthProvider';
import { useCases } from '../../../core/di/useCases';

export const useProfile = (uid) => {
  const { user: currentUser, refreshUser } = useAuth();
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [participations, setParticipations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isOwnProfile = currentUser?.uid === uid;

  const refresh = useCallback(async () => {
    if (!uid) return;
    
    setLoading(true);
    try {
      // Utiliser le use case pour récupérer le profil utilisateur
      const userData = await useCases.getUserProfile.execute(uid);
      setUser(userData);
      
      // Si c'est le profil actuel, mettons également à jour dans le contexte d'authentification
      if (isOwnProfile && refreshUser) {
        refreshUser();
      }
      
      // Vérifier si on peut voir le contenu de l'utilisateur (posts et participations)
      const canViewUserContent = isOwnProfile || 
        userData.preferences?.profileVisibility === 'everyone' || 
        (userData.preferences?.profileVisibility === 'followers' && currentUser?.following?.includes(uid)); 
      // Pour nobody, même les followers ne peuvent pas voir les posts/participations
      
      if (canViewUserContent) {
        // Utiliser getUserPosts pour obtenir les posts de l'utilisateur
        const userPosts = await useCases.repositories.users.getUserPosts(uid);
        // Utiliser getUserParticipations pour obtenir les participations de l'utilisateur
        const userParticipations = await useCases.repositories.users.getUserParticipations(uid);
        
        setPosts(userPosts);
        setParticipations(userParticipations);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [uid, currentUser, isOwnProfile, refreshUser]);

  useEffect(() => {
    // Exécuter seulement au montage du composant
    const loadInitialData = async () => {
      if (!uid) return;
      
      setLoading(true);
      try {
        // Utiliser le use case pour récupérer le profil utilisateur
        const userData = await useCases.getUserProfile.execute(uid);
        setUser(userData);
        
        // Vérifier si on peut voir le contenu de l'utilisateur (posts et participations)
        const canViewUserContent = isOwnProfile || 
          userData.preferences?.profileVisibility === 'everyone' || 
          (userData.preferences?.profileVisibility === 'followers' && currentUser?.following?.includes(uid)); 
        // Pour nobody, même les followers ne peuvent pas voir les posts/participations
        
        if (canViewUserContent) {
          // Utiliser getUserPosts pour obtenir les posts de l'utilisateur
          const userPosts = await useCases.repositories.users.getUserPosts(uid);
          // Utiliser getUserParticipations pour obtenir les participations de l'utilisateur
          const userParticipations = await useCases.repositories.users.getUserParticipations(uid);
          
          setPosts(userPosts);
          setParticipations(userParticipations);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]); // Dépendre seulement de uid pour éviter la boucle

  return {
    user,
    posts,
    participations,
    loading,
    error,
    isOwnProfile,
    refresh
  };
}; 